import { useLanguage } from "@/context/LanguageContext";
import { FileText, ShoppingCart, Users, Link2, Calculator, BarChart3 } from "lucide-react";

// Tüm görseller lokal /images/ klasöründen — harici CDN bağımlılığı yok (LCP iyileştirmesi)
const screenshots = {
  main: "/images/edited_dashboard.png",
  details: "/images/edited_offers.png",
  bayi: "/images/edited_dealers.png",
  order: "/images/quote_management.png",
};

export function FeaturesSection() {
  const { t } = useLanguage();

  const features = [
    { icon: FileText, key: "quoteManagement", color: "bg-blue-50 text-blue-600" },
    { icon: ShoppingCart, key: "orderManagement", color: "bg-cyan-50 text-cyan-600" },
    { icon: Users, key: "dealerManagement", color: "bg-violet-50 text-violet-600" },
    { icon: Link2, key: "supplierIntegration", color: "bg-amber-50 text-amber-600" },
    { icon: Calculator, key: "preAccounting", color: "bg-rose-50 text-rose-600" },
    { icon: BarChart3, key: "analytics", color: "bg-cyan-50 text-cyan-600" },
  ];

  return (
    <section id="features" data-testid="features-section" className="py-20 sm:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
            {t("features.overline")}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {t("features.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">
            {t("features.subtitle")}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          {/* Large card - Dashboard */}
          <div className="md:col-span-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 hover:border-slate-300 transition-colors group" data-testid="feature-dashboard">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${features[0].color}`}>
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{t("features.quoteManagement")}</h3>
                <p className="text-sm text-slate-500">{t("features.quoteDesc")}</p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden border border-slate-100">
              <img src={screenshots.order} alt={t("imageAlt.quoteManagement")} width="800" height="500" className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500" loading="lazy" decoding="async" />
            </div>
          </div>

          {/* Right small cards */}
          <div className="md:col-span-4 flex flex-col gap-4 sm:gap-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors flex-1" data-testid="feature-orders">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${features[1].color}`}>
                <ShoppingCart className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{t("features.orderManagement")}</h3>
              <p className="text-sm text-slate-500 mt-1">{t("features.orderDesc")}</p>
              <div className="mt-3 rounded-lg overflow-hidden opacity-80">
                <img src="/images/order_factory.png" alt={t("imageAlt.orderManagement")} width="400" height="280" className="w-full h-auto" loading="lazy" decoding="async" />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors flex-1" data-testid="feature-dealers">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${features[2].color}`}>
                <Users className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{t("features.dealerManagement")}</h3>
              <p className="text-sm text-slate-500 mt-1">{t("features.dealerDesc")}</p>
              <div className="mt-3 rounded-lg overflow-hidden opacity-80">
                <img src="/images/dealer_network.png" alt={t("imageAlt.dealerNetwork")} width="400" height="280" className="w-full h-auto" loading="lazy" decoding="async" />
              </div>
            </div>
          </div>

          {/* Bottom row */}
          <div className="md:col-span-4 rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors group" data-testid="feature-supplier">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${features[3].color}`}>
              <Link2 className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">{t("features.supplierIntegration")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("features.supplierDesc")}</p>
            <div className="mt-4 rounded-xl overflow-hidden border border-slate-100">
              <img src={screenshots.bayi} alt={t("imageAlt.supplierIntegration")} width="400" height="280" className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500" loading="lazy" decoding="async" />
            </div>
          </div>

          <div className="md:col-span-4 rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors group" data-testid="feature-accounting">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${features[4].color}`}>
              <Calculator className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">{t("features.preAccounting")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("features.preAccountingDesc")}</p>
            <div className="mt-4 rounded-xl overflow-hidden border border-slate-100">
              <img src={screenshots.details} alt={t("imageAlt.preAccounting")} width="400" height="280" className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500" loading="lazy" decoding="async" />
            </div>
          </div>

          <div className="md:col-span-4 rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors group" data-testid="feature-analytics">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${features[5].color}`}>
              <BarChart3 className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">{t("features.analytics")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("features.analyticsDesc")}</p>
            <div className="mt-4 rounded-xl overflow-hidden border border-slate-100">
              <img src="/images/analytics_dashboard.png" alt={t("imageAlt.analytics")} width="400" height="280" className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
